import type {
  AdminData,
} from "./hooks";
import type {
  AdminDaytonaSandbox,
  AdminGcpInstance,
  AdminLambdaMicrovm,
  AdminSprite,
  HealthState,
  WorkspaceState,
} from "./types";

// Every provider spells its states differently. These normalisers are the one
// place that mapping lives, so the tab counts, the summary strip, and the
// per-provider tables can never drift apart on what "running" means.

export function spriteState(sprite: AdminSprite): WorkspaceState {
  if (sprite.status === "running") return "running";
  if (sprite.status === "persisted") return "stopped";
  if (sprite.status === "orphaned") return "orphaned";
  return "unknown";
}

export function daytonaState(sandbox: AdminDaytonaSandbox): WorkspaceState {
  const state = sandbox.state.toLowerCase();
  if (state === "started" || state === "running") return "running";
  if (state === "stopped" || state === "paused" || state === "archived") return "stopped";
  if (state === "destroyed" || state === "deleted") return "terminal";
  return "unknown";
}

export function lambdaState(microvm: AdminLambdaMicrovm): WorkspaceState {
  const state = microvm.state.toUpperCase();
  if (state === "RUNNING") return "running";
  if (state === "SUSPENDED") return "stopped";
  if (state === "TERMINATED" || state === "TERMINATING") return "terminal";
  return "unknown";
}

export function gcpState(instance: AdminGcpInstance): WorkspaceState {
  const state = instance.state.toUpperCase();
  if (state === "RUNNING") return "running";
  if (state === "STOPPED") return "stopped";
  // The old page treated TERMINATED as just another stopped state. It is not:
  // a terminated instance cannot be restarted and its home disk may be gone.
  if (state === "TERMINATED") return "terminal";
  if (state === "MISSING") return "orphaned";
  return "unknown";
}

/** Only GCP reports Core health today; everything else is unknown until it does. */
export function gcpHealth(instance: AdminGcpInstance): HealthState {
  return instance.core.status;
}

export type AdminSummary = {
  workspaces: number;
  running: number;
  stopped: number;
  orphaned: number;
  agents: number;
};

export function summarise(data: Pick<AdminData, "overview" | "daytona" | "lambda" | "gcp">): AdminSummary {
  const states: WorkspaceState[] = [
    ...(data.overview?.sprites ?? []).map(spriteState),
    ...(data.daytona?.sandboxes ?? []).map(daytonaState),
    ...(data.lambda?.microvms ?? []).map(lambdaState),
    ...(data.gcp?.instances ?? []).map(gcpState),
  ];

  const agentsFromCore = (data.overview?.users ?? []).filter((user) => user.hasActiveAgent).length;
  const agentsFromGcp = (data.gcp?.users ?? []).filter((user) => user.hasActiveAgent).length;

  return {
    workspaces: states.length,
    running: states.filter((state) => state === "running").length,
    stopped: states.filter((state) => state === "stopped").length,
    // Orphaned only. Terminal used to be folded in here, but now that the two
    // are distinct states the label would be counting things that are not
    // orphans. A dead instance still shows its own danger row in the table.
    orphaned: states.filter((state) => state === "orphaned").length,
    agents: Math.max(agentsFromCore, agentsFromGcp),
  };
}

export type ProviderCounts = {
  users: number;
  sprites: number;
  daytona: number;
  lambda: number;
  gcp: number;
};

export function providerCounts(
  data: Pick<AdminData, "overview" | "daytona" | "lambda" | "gcp">,
): ProviderCounts {
  return {
    users: data.overview?.users.length ?? 0,
    sprites: data.overview?.sprites.length ?? 0,
    daytona: data.daytona?.sandboxes.length ?? 0,
    lambda: data.lambda?.microvms.length ?? 0,
    gcp: data.gcp?.instances.length ?? 0,
  };
}
